// src/quiz/gre-mock9/quantitativeData.js
// GRE Mock 9 — Quantitative Reasoning.
// Section 1: 12 questions (21 min). Section 2: 15 questions (26 min).
// Question format: see ../greQuestionTypes.js
import { QC_OPTIONS } from "../greQuestionTypes";

const QC_QUESTION = "Compare Quantity A and Quantity B, using additional information centered above the two quantities if such information is given.";
const [QC_A, QC_B, QC_EQUAL] = QC_OPTIONS;

// ================= SECTION 1 =================

export const section1Questions = [
  {
    id: 1,
    question: QC_QUESTION,
    quantityA: "The least prime number greater than 30",
    quantityB: "The greatest prime number less than 36",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "31 is prime, so it is the least prime above 30. Checking down from 35: 35 = 5 × 7, 34 and 32 are even, 33 = 3 × 11, and 31 is prime. Both quantities are 31.",
  },
  {
    id: 2,
    question: QC_QUESTION,
    quantityA: "45% of 240",
    quantityB: "100",
    options: QC_OPTIONS,
    answer: QC_A,
    explanation: "45% of 240 = 0.45 × 240 = 108, which is greater than 100.",
  },
  {
    id: 3,
    question: "A car gets 30 miles per gallon using gasoline that costs $3.20 per gallon. What is the cost of the gasoline needed to drive the car 450 miles?",
    options: ["$38", "$44", "$48", "$54", "$60"],
    answer: "$48",
    explanation: "450 miles ÷ 30 miles per gallon = 15 gallons, and 15 × $3.20 = $48.",
  },
  {
    id: 4,
    question: "If x = 3y − 2 and y = 4, what is the value of x?",
    options: ["8", "10", "12", "14"],
    answer: "10",
    explanation: "Substitute y = 4: x = 3(4) − 2 = 12 − 2 = 10.",
  },
  {
    id: 5,
    question: "Which of the following integers is a multiple of both 2 and 3?",
    options: ["9", "16", "18", "21"],
    answer: "18",
    explanation: "A multiple of both 2 and 3 is a multiple of 6. Only 18 = 6 × 3 qualifies; 9 and 21 are odd, and 16 is not divisible by 3.",
  },
  {
    id: 6,
    question: "One pen costs $0.30 and one marker costs $0.45. What is the total cost of 20 pens and 50 markers?",
    options: ["$26.50", "$27.50", "$28.50", "$30.50"],
    answer: "$28.50",
    explanation: "20 × $0.30 = $6.00 and 50 × $0.45 = $22.50. Total = $28.50.",
  },
  {
    id: 7,
    question: "Working alone, machine A produces k liters of a chemical in 12 minutes, and machine B produces k liters in 24 minutes. Working together at their constant rates, how many minutes do they need to produce k liters?",
    options: ["6", "8", "12", "18"],
    answer: "8",
    explanation: "Per minute, A makes k/12 and B makes k/24, together 3k/24 = k/8. So k liters take 8 minutes.",
  },
  {
    id: 8,
    question: "A store had sales of $500,000 in 2006. Sales increased by 20% in 2007 and then decreased by 10% in 2008. What were the sales in 2008?",
    options: ["$500,000", "$525,000", "$540,000", "$560,000", "$600,000"],
    answer: "$540,000",
    explanation: "2007: $500,000 × 1.2 = $600,000. 2008: $600,000 × 0.9 = $540,000.",
  },
  {
    id: 9,
    question: "The ratio of A to B is 2 : 3 and the ratio of B to C is 4 : 5. What is the ratio of A to C?",
    options: ["2 : 5", "8 : 15", "3 : 5", "4 : 5"],
    answer: "8 : 15",
    explanation: "Make B the same in both ratios: A : B = 8 : 12 and B : C = 12 : 15. So A : C = 8 : 15.",
  },
  {
    id: 10,
    question: "Which of the following is equivalent to 4(3x − 2) − 5x?",
    options: ["7x − 8", "7x + 8", "17x − 8", "8x − 8"],
    answer: "7x − 8",
    explanation: "Distribute: 12x − 8 − 5x = 7x − 8.",
  },
  {
    id: 11,
    question: "What is the value of (3x − 1)/(x + 1) when x = 3?",
    options: ["1", "2", "3", "4"],
    answer: "2",
    explanation: "(3·3 − 1)/(3 + 1) = 8/4 = 2.",
  },
  {
    id: 12,
    question: QC_QUESTION,
    given: "x is an integer greater than 1.",
    quantityA: "x²",
    quantityB: "x",
    options: QC_OPTIONS,
    answer: QC_A,
    explanation: "x² = x · x, and multiplying x by a number greater than 1 makes it larger. Since x ≥ 2, x² > x always.",
  },
];

// ================= SECTION 2 =================

export const section2Questions = [
  {
    id: 13,
    question: "What is the median of the data set 9, 3, 7, 12, 5?",
    options: ["5", "7", "9", "12"],
    answer: "7",
    explanation: "Put the values in order: 3, 5, 7, 9, 12. The middle (third) value is 7.",
  },
  {
    id: 14,
    question: "The average of six numbers is 8. If one of the numbers is 14, what is the average of the remaining five numbers?",
    options: ["6.4", "6.8", "7", "7.2"],
    answer: "6.8",
    explanation: "The six numbers sum to 6 × 8 = 48. Removing 14 leaves 34, and 34 ÷ 5 = 6.8.",
  },
  {
    id: 15,
    question: "A survey found that 35% of 400 participants preferred tea over coffee. How many participants preferred tea?",
    options: ["120", "140", "160", "180"],
    answer: "140",
    explanation: "35% of 400 = 0.35 × 400 = 140.",
  },
  {
    id: 16,
    question: "What are the solutions of x² − 7x + 12 = 0?",
    options: ["2 and 6", "3 and 4", "−3 and −4", "1 and 12"],
    answer: "3 and 4",
    explanation: "Factor: (x − 3)(x − 4) = 0, so x = 3 or x = 4. (−3 and −4 would solve x² + 7x + 12 = 0.)",
  },
  {
    id: 17,
    question: "What is the greatest common divisor of 48 and 72?",
    options: ["12", "18", "24", "36"],
    answer: "24",
    explanation: "48 = 2⁴ × 3 and 72 = 2³ × 3². The shared factors are 2³ × 3 = 24.",
  },
  {
    id: 18,
    question: "If a/b = 5/6 and b = 18, what is the value of a?",
    options: ["12", "15", "16", "18"],
    answer: "15",
    explanation: "Multiply both sides by b: a = (5/6) × 18 = 15.",
  },
  {
    id: 19,
    question: "The price of an item is increased by 25% and then decreased by 25%. What is the net change in price?",
    options: ["No change", "6.25% decrease", "6.25% increase", "12.5% decrease"],
    answer: "6.25% decrease",
    explanation: "1.25 × 0.75 = 0.9375, so the final price is 93.75% of the original: a 6.25% decrease. The decrease applies to a larger base, so the changes don't cancel.",
  },
  {
    id: 20,
    question: "In a right triangle, one of the non-right angles measures 40°. What is the measure of the other non-right angle?",
    options: ["40°", "50°", "60°", "80°"],
    answer: "50°",
    explanation: "The angles sum to 180°, and one is 90°, so the two acute angles sum to 90°: 90° − 40° = 50°.",
  },
  {
    id: 21,
    question: QC_QUESTION,
    quantityA: "3/7",
    quantityB: "4/9",
    options: QC_OPTIONS,
    answer: QC_B,
    explanation: "Cross-multiply: 3 × 9 = 27 and 4 × 7 = 28. Since 27 < 28, 3/7 < 4/9.",
  },
  {
    id: 22,
    question: "A rectangle has a perimeter of 36 and its length is twice its width. What is its area?",
    options: ["54", "64", "72", "81"],
    answer: "72",
    explanation: "With width w and length 2w, the perimeter is 6w = 36, so w = 6 and the length is 12. Area = 6 × 12 = 72.",
  },
  {
    id: 23,
    question: "A sum of $2,000 is invested at 10% interest compounded annually. What is the total amount after 2 years?",
    options: ["$2,400", "$2,420", "$2,440", "$2,200"],
    answer: "$2,420",
    explanation: "$2,000 × 1.1 × 1.1 = $2,420. ($2,400 is simple interest; compounding adds interest on the first year's $200.)",
  },
  {
    id: 24,
    question: "In how many ways can a committee of 2 people be chosen from a group of 7 people?",
    options: ["14", "21", "35", "42"],
    answer: "21",
    explanation: "Order doesn't matter in a committee: C(7, 2) = (7 × 6)/2 = 21. (42 counts ordered pairs.)",
  },
  {
    id: 25,
    question: "If 2^(x + 2) = 64, what is the value of x?",
    options: ["3", "4", "5", "6"],
    answer: "4",
    explanation: "64 = 2⁶, so x + 2 = 6 and x = 4.",
  },
  {
    id: 26,
    question: QC_QUESTION,
    given: "x + y = 10 and xy = 21.",
    quantityA: "x² + y²",
    quantityB: "58",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "x² + y² = (x + y)² − 2xy = 100 − 42 = 58. (The numbers are 3 and 7: 9 + 49 = 58.)",
  },
  {
    id: 27,
    question: "A cyclist rides half of a journey's distance at 30 km/h and the other half at 60 km/h. What is the average speed for the whole journey?",
    options: ["40 km/h", "45 km/h", "48 km/h", "50 km/h"],
    answer: "40 km/h",
    explanation: "Take a 120 km journey: 60 km at 30 km/h takes 2 hours, 60 km at 60 km/h takes 1 hour. 120 km ÷ 3 hours = 40 km/h. More time is spent at the slower speed, so the answer is below 45.",
  },
];
